import { useState } from 'react';
import { BORNES_PROFIL, CHAMPS_DU_PROFIL, FEUILLES_DE_PROFIL, type ChampDuProfil } from '@nextilt/commun';
import type { ChampsDuProfil } from '../partage/api/types';
import { useAuPouce } from '../partage/surface';
import {
  ENREGISTRER_LE_PROFIL,
  EXEMPLES_DES_CHAMPS_DU_PROFIL,
  MOTS_DES_CHAMPS_DU_PROFIL,
  PHRASE_DU_FORMULAIRE_DU_PROFIL,
  PLUS_TARD_DU_PROFIL,
  TITRES_DES_FEUILLES_DU_PROFIL,
  TITRE_DU_FORMULAIRE_DU_PROFIL,
} from '../partage/vocabulaire';
import { ChoixDEnergie } from './ChoixDEnergie';
import { CHAMPS_DE_LA_FEUILLE, casesDuFormulaire, formulaireSOuvreSeul } from './reglesDuProfil';
import { formulaireDejaFerme, useProfil } from './useProfil';
import './FormulaireDuProfil.css';

/**
 * Le premier formulaire du profil (chantier profil, ticket 03, décisions 5 et 7 d'Igor) : une
 * feuille scotchée par-dessus l'écran voilé, comme la connexion (`comptes/AccueilSansCompte.tsx`).
 * Les champs et leurs mots sont ceux de la page Profil (`CHAMPS_DE_LA_FEUILLE`,
 * `MOTS_DES_CHAMPS_DU_PROFIL`), feuille par feuille dans l'ordre de la page ; chaque case peut
 * rester vide.
 *
 * « Enregistrer » envoie le profil entier s'il a changé, puis note le formulaire vu ; « Plus tard »
 * le note seulement vu. Les deux le ferment, et il ne s'ouvre plus seul. Ni un clic à côté ni
 * Échap ne le ferment : rien de ce qui est écrit dedans ne se perd par mégarde.
 */
export function FormulaireDuProfil({ onFermer }: { onFermer: () => void }) {
  const profil = useProfil();
  const lu = profil.lecture;
  if (!lu) return null;
  return <FeuilleDuFormulaire initial={lu.profil} gestes={profil} onFermer={onFermer} />;
}

/**
 * Le formulaire qui s'ouvre seul, sur l'accueil (et sur la page Profil pour un compte qui y arrive
 * sans être passé par l'accueil) : à l'ordinateur, tant que le serveur dit qu'il n'a pas été vu.
 */
export function FormulaireALaPremiereVisite() {
  const auPouce = useAuPouce();
  const profil = useProfil();
  const [ferme, setFerme] = useState(false);
  const lu = profil.lecture;
  if (!lu || ferme || formulaireDejaFerme() || !formulaireSOuvreSeul(auPouce, lu.formulaireAMontrer)) return null;
  return <FeuilleDuFormulaire initial={lu.profil} gestes={profil} onFermer={() => setFerme(true)} />;
}

function FeuilleDuFormulaire({
  initial,
  gestes,
  onFermer,
}: {
  initial: ChampsDuProfil;
  gestes: ReturnType<typeof useProfil>;
  onFermer: () => void;
}) {
  // Pré-rempli une fois, à l'ouverture : ce qui est tapé ensuite reste à l'écran.
  const [cases, setCases] = useState(() => casesDuFormulaire(initial));
  const [enCours, setEnCours] = useState(false);

  const plusTard = () => {
    gestes.noterLeFormulaireVu();
    onFermer();
  };
  const enregistrer = async () => {
    if (enCours) return;
    setEnCours(true);
    const ferme = await gestes.enregistrerLeFormulaire(cases);
    setEnCours(false);
    if (ferme) onFermer();
  };

  return (
    <div className="formulaire-du-profil">
      <div className="formulaire-du-profil-voile" aria-hidden="true" />
      <form
        className="formulaire-du-profil-feuille"
        role="dialog"
        aria-modal="true"
        aria-labelledby="formulaire-du-profil-titre"
        onSubmit={(e) => {
          e.preventDefault();
          void enregistrer();
        }}
      >
        <h2 id="formulaire-du-profil-titre">{TITRE_DU_FORMULAIRE_DU_PROFIL}</h2>
        <p className="formulaire-du-profil-phrase">{PHRASE_DU_FORMULAIRE_DU_PROFIL}</p>

        {FEUILLES_DE_PROFIL.map((feuille) => (
          <fieldset key={feuille} className="formulaire-du-profil-groupe" data-feuille={feuille}>
            <legend>{TITRES_DES_FEUILLES_DU_PROFIL[feuille]}</legend>
            {CHAMPS_DE_LA_FEUILLE[feuille].map((champ) => (
              <Case
                key={champ}
                champ={champ}
                valeur={cases[champ]}
                focus={champ === CHAMPS_DU_PROFIL[0]}
                onChange={(valeur) => setCases((c) => ({ ...c, [champ]: valeur }))}
              />
            ))}
          </fieldset>
        ))}

        {gestes.annonce && (
          <p className="formulaire-du-profil-annonce" role="status">
            {gestes.annonce}
          </p>
        )}

        <div className="formulaire-du-profil-gestes">
          <button type="button" className="formulaire-du-profil-plus-tard" onClick={plusTard} disabled={enCours}>
            {PLUS_TARD_DU_PROFIL}
          </button>
          <button type="submit" className="formulaire-du-profil-enregistrer" disabled={enCours}>
            {ENREGISTRER_LE_PROFIL}
          </button>
        </div>
      </form>
    </div>
  );
}

/** Une case : le nom du champ, celui de la page, à gauche ; ce qu'on écrit à droite. */
function Case({
  champ,
  valeur,
  focus,
  onChange,
}: {
  champ: ChampDuProfil;
  valeur: string;
  focus: boolean;
  onChange: (valeur: string) => void;
}) {
  const id = `formulaire-du-profil-${champ}`;
  const nom = MOTS_DES_CHAMPS_DU_PROFIL[champ];
  // L'énergie se choisit en trois boutons (retour d'Igor du 2026-10-21).
  if (champ === 'courage') {
    return (
      <div className="formulaire-du-profil-case" data-champ={champ}>
        <span className="formulaire-du-profil-nom">{nom}</span>
        <ChoixDEnergie nom={nom} valeur={valeur === '' ? null : valeur} onChoisir={onChange} />
      </div>
    );
  }
  return (
    <div className="formulaire-du-profil-case" data-champ={champ}>
      <label htmlFor={id}>{nom}</label>
      <input
        id={id}
        value={valeur}
        placeholder={EXEMPLES_DES_CHAMPS_DU_PROFIL[champ]}
        maxLength={BORNES_PROFIL.texteMax}
        autoFocus={focus}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
